import type { ReactNode } from 'react';
import { cn } from './cn.js';

/** A live instrument card: small caps label, optional status, body. */
export function Instrument({ label, status, action, tone = 'glass', className, children }: { label: string; status?: ReactNode; action?: ReactNode; tone?: 'glass' | 'smoke'; className?: string; children: ReactNode }) {
  return (
    <div className={cn('nw-instrument', tone === 'smoke' && 'nw-instrument-smoke', className)}>
      <div className="flex items-center gap-2">
        <span className="nw-label">{label}</span>
        {status ? <span className="flex items-center gap-1.5 text-[11px] text-[var(--nw-quiet)]">{status}</span> : null}
        <span className="ms-auto">{action}</span>
      </div>
      {children}
    </div>
  );
}

/** Large light numeral with unit, as on a dashboard instrument. */
export function Readout({ value, unit, className }: { value: ReactNode; unit?: ReactNode; className?: string }) {
  return (
    <p className={cn('flex items-baseline gap-1.5', className)}>
      <span className="nw-readout">{value}</span>
      {unit ? <span className="text-sm text-[var(--nw-quiet)]">{unit}</span> : null}
    </p>
  );
}

/** A row of vertical ticks, filled up to `value` of `max` (ref: tick-bar gauges). */
export function TickBars({ value, max, count = 32, label }: { value: number; max: number; count?: number; label: string }) {
  const filled = max > 0 ? Math.round((Math.min(value, max) / max) * count) : 0;
  return (
    <div role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label} className="nw-ticks">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={cn('nw-tick', i < filled && 'nw-tick-on', i === filled - 1 && 'nw-tick-head')} />
      ))}
    </div>
  );
}

/** Dotted ring split into labelled segments (ref: variability ring). */
export function DottedRing({ segments, size = 120, centre }: { segments: Array<{ value: number; color: string; label: string }>; size?: number; centre?: ReactNode }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const dots = 48;
  const r = size / 2 - 6;
  let acc = 0;
  const colours = Array.from({ length: dots }, (_, i) => {
    const t = (i + 0.5) / dots;
    acc = 0;
    for (const s of segments) {
      acc += s.value / total;
      if (t <= acc) return s.color;
    }
    return 'var(--nw-line-strong)';
  });
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} width={size} height={size} role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(', ')}>
        {colours.map((c, i) => {
          const a = (i / dots) * Math.PI * 2 - Math.PI / 2;
          return <circle key={i} cx={Math.cos(a) * r} cy={Math.sin(a) * r} r={2.4} fill={segments.length && total > 0 ? c : 'var(--nw-line-strong)'} />;
        })}
        <circle r={r - 14} fill="none" stroke="var(--nw-line)" strokeDasharray="1 5" />
      </svg>
      {centre ? <div className="absolute inset-0 grid place-items-center text-center">{centre}</div> : null}
    </div>
  );
}
